import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RestSlice } from 'src/redux/rest.slice';
import { EmailNotificationSetting } from '@rcp/types';
import { apiService, localizationService, Resource, urlService } from 'src/services';
import { alertService } from 'src/redux/alert';

interface EmailNotificationsState {
	data: {
		result: any[];
		page: number;
		size: number;
		total: number;
	};
	loading: boolean;
	error: string | null;
	selected: any[];
	allTypes: Array<{
		emailNotificationTypeId: number;
		notificationType: string;
	}>;
	originalNotifications: EmailNotificationSetting[];
	fullDataset: EmailNotificationSetting[]; 
	userModifications: Record<string, boolean>;
}

const initialState: EmailNotificationsState = {
	data: {
		result: [],
		page: 1,
		size: 10,
		total: 0
	},
	loading: false,
	error: null,
	selected: [],
	allTypes: [],
	originalNotifications: [],
	fullDataset: [],
	userModifications: {}
};

// Async thunk to fetch email notifications
export const fetchEmailNotifications = createAsyncThunk(
	'emailNotifications/fetchPage',
	async (params: any) => {
		try {
			const queryParts: string[] = [];
			
			queryParts.push(`page=${params.page || 1}`);
			queryParts.push(`size=${params.size || 10}`);
			
			if (params.search) {
				queryParts.push(`search=${encodeURIComponent(params.search)}`);
			}
			
			if (params.sort) {
				queryParts.push(`sort=${encodeURIComponent(params.sort)}`);
			}

			const ippUserId = params.userProfileId;
			const baseUrl = urlService.getAuthorityResourceApiUrl(Resource.EmailNotifications, ippUserId);
			const url = `${baseUrl}?${queryParts.join('&')}`;
			const response = await apiService.getResource<EmailNotificationSetting[]>(url);

			return response;
		} catch (error: any) {
			throw new Error(error.message || 'Failed to fetch email notifications');
		}
	}
);

export const saveEmailNotificationSettings = createAsyncThunk<
	void,
	{ userProfileId: number; settings: Record<number, number[]> }
>('emailNotifications/save', async ({ userProfileId, settings }) => {
	try {
		const url = `${urlService.getAuthorityResourceApiUrl(Resource.UpdateEmailNotification, userProfileId)}`;
		await apiService.httpPost(url, settings);
		alertService.addSuccess(
			localizationService.getLocalizedString('ipp.accountPortal.messages.emailNotificationUpdateSuccess')
		);
	} catch (error) {
		alertService.addError(
			localizationService.getLocalizedString('ipp.accountPortal.messages.emailNotificationUpdateError')
		);
	}
});

const emailNotificationsSlice = createSlice({
	name: 'emailNotifications',
	initialState,
	reducers: {
		toggleNotification: (state, action: PayloadAction<{ orgId: number; typeId: number; checked: boolean }>) => {
			const { orgId, typeId, checked } = action.payload;
			
			const modificationKey = `${orgId}_${typeId}`;
			state.userModifications[modificationKey] = checked;
			
			const rowIndex = state.data.result.findIndex(row => row.organizationId === orgId);
			if (rowIndex !== -1) {
				state.data.result[rowIndex][`type_${typeId}`] = checked;
			}
			
			const orgNotification = state.originalNotifications.find(n => n.organizationId === orgId);
			if (orgNotification) {
				const notificationType = orgNotification.notificationTypes?.find(
					nt => nt.emailNotificationTypeId === typeId
				);
				if (notificationType) {
					notificationType.isSubscribed = checked;
				}
			}
			
			const fullOrgNotification = state.fullDataset.find(n => n.organizationId === orgId);
			if (fullOrgNotification) {
				const fullNotificationType = fullOrgNotification.notificationTypes?.find(
					nt => nt.emailNotificationTypeId === typeId
				);
				if (fullNotificationType) {
					fullNotificationType.isSubscribed = checked;
				}
			}
		},
		toggleAllNotificationsForOrg: (state, action: PayloadAction<{ orgId: number; checked: boolean }>) => {
			const { orgId, checked } = action.payload;
			
			state.allTypes.forEach(type => {
				const modificationKey = `${orgId}_${type.emailNotificationTypeId}`;
				state.userModifications[modificationKey] = checked;
			});
			
			const rowIndex = state.data.result.findIndex(row => row.organizationId === orgId);
			if (rowIndex !== -1) {
				state.allTypes.forEach(type => {
					state.data.result[rowIndex][`type_${type.emailNotificationTypeId}`] = checked;
				});
			}
			const orgNotification = state.originalNotifications.find(n => n.organizationId === orgId);
			if (orgNotification) {
				orgNotification.notificationTypes?.forEach(nt => {
					nt.isSubscribed = checked;
				});
			}
			
			const fullOrgNotification = state.fullDataset.find(n => n.organizationId === orgId);
			if (fullOrgNotification) {
				fullOrgNotification.notificationTypes?.forEach(nt => {
					nt.isSubscribed = checked;
				});
			}
		},
		toggleAllOrganizationsForType: (state, action: PayloadAction<{ typeId: number; checked: boolean }>) => {
			const { typeId, checked } = action.payload;

			state.data.result.forEach(row => {
				const orgId = row.organizationId;
				
				const modificationKey = `${orgId}_${typeId}`;
				state.userModifications[modificationKey] = checked;
				row[`type_${typeId}`] = checked;
				
				const fullOrgNotification = state.fullDataset.find(n => n.organizationId === orgId);
				if (fullOrgNotification) {
					const notificationType = fullOrgNotification.notificationTypes?.find(nt => nt.emailNotificationTypeId === typeId);
					if (notificationType) {
						notificationType.isSubscribed = checked;
					}
				}
				
				const origOrgNotification = state.originalNotifications.find(n => n.organizationId === orgId);
				if (origOrgNotification) {
					const notificationType = origOrgNotification.notificationTypes?.find(nt => nt.emailNotificationTypeId === typeId);
					if (notificationType) {
						notificationType.isSubscribed = checked;
					}
				}
			});
		},
		resetState: state => {
			state.data = initialState.data;
			state.loading = false;
			state.error = null;
			state.selected = [];
			state.allTypes = [];
			state.originalNotifications = [];
			state.fullDataset = [];
			state.userModifications = {};
		},
		clearError: state => {
			state.error = null;
		},
		clearModifications: state => {
			state.userModifications = {};
		}
	},
	extraReducers: builder => {
		builder
			// Fetch email notifications
			.addCase(fetchEmailNotifications.pending, state => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchEmailNotifications.fulfilled, (state, action) => {
				state.loading = false;

				// Handle both paginated and non-paginated API responses
				const response = action.payload;
				const page = (response as any).page || 1;
				const size = (response as any).size || 10;
				const total = (response as any).total || (Array.isArray(response) ? response.length : (response as any).result?.length || 0);
				const result = Array.isArray(response) ? response : (response as any).result || [];
				
				state.originalNotifications = result || [];

				if (state.fullDataset.length === 0) {
					state.fullDataset = result || [];
				} else {
					const currentPageOrgIds = new Set(result.map((r: any) => r.organizationId));
					state.fullDataset = state.fullDataset.filter(org => !currentPageOrgIds.has(org.organizationId));
					state.fullDataset = [...state.fullDataset, ...result];
				}
				
				// Extract all unique notification types
				const allTypesMap = new Map();
				state.fullDataset.forEach(setting => {
					setting.notificationTypes?.forEach(nt => {
						allTypesMap.set(nt.emailNotificationTypeId, {
							emailNotificationTypeId: nt.emailNotificationTypeId,
							notificationType: nt.notificationType
						});
					});
				});
				
				state.allTypes = Array.from(allTypesMap.values());
				
				// Transform data for DataGrid, applying user modifications if they exist
				const gridData = state.originalNotifications.map(setting => {
					const orgId = setting.organizationId;
					const row: Record<string, any> = {
						organizationId: orgId,
						industryName: setting.industryName,
						// Initialize all types as false
						...state.allTypes.reduce((acc, t) => {
							acc[`type_${t.emailNotificationTypeId}`] = false;
							return acc;
						}, {} as Record<string, boolean>)
					};
					
					// Fill in the subscription states, checking for user modifications first
					setting.notificationTypes?.forEach(nt => {
						const modificationKey = `${orgId}_${nt.emailNotificationTypeId}`;
						
						// Check if user has modified this checkbox
						if (modificationKey in state.userModifications) {
							const userModifiedValue = state.userModifications[modificationKey];
							row[`type_${nt.emailNotificationTypeId}`] = userModifiedValue;
							nt.isSubscribed = userModifiedValue;
						} else {
							row[`type_${nt.emailNotificationTypeId}`] = nt.isSubscribed;
						}
					});
					
					return row;
				});

				state.data = {
					result: gridData,
					page: page || 1,
					size: size || 10,
					total: total || 0
				};
			})
			.addCase(fetchEmailNotifications.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error.message || 'Failed to fetch email notifications';
			})
			// Save email notification settings
			.addCase(saveEmailNotificationSettings.pending, state => {
				state.loading = true;
				state.error = null;
			})
			.addCase(saveEmailNotificationSettings.fulfilled, state => {
				state.loading = false;
				// Clear user modifications after successful save
				state.userModifications = {};
			})
			.addCase(saveEmailNotificationSettings.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error.message || 'Failed to save email notification settings';
			});
	}
});

export const {
	toggleNotification,
	toggleAllNotificationsForOrg,
	toggleAllOrganizationsForType,
	resetState,
	clearError,
	clearModifications
} = emailNotificationsSlice.actions;
export const emailNotificationsRestSlice: RestSlice<EmailNotificationSetting> = ({
	fetchPage: fetchEmailNotifications
} as unknown) as RestSlice<EmailNotificationSetting>;

// Selectors
export const selectEmailNotificationsState = (state: any) => state.emailNotifications;
export const selectEmailNotificationsData = (state: any) => state.emailNotifications.data;
export const selectEmailNotificationsLoading = (state: any) => state.emailNotifications.loading;
export const selectEmailNotificationsError = (state: any) => state.emailNotifications.error;
export const selectAllNotificationTypes = (state: any) => state.emailNotifications.allTypes;
export const selectOriginalNotifications = (state: any) => state.emailNotifications.originalNotifications;
export const selectFullDataset = (state: any) => state.emailNotifications.fullDataset;
export const selectUserModifications = (state: any) => state.emailNotifications.userModifications;
export const selectHasUnsavedChanges = (state: any) => Object.keys(state.emailNotifications.userModifications).length > 0;

export default emailNotificationsSlice.reducer;
